import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, Calendar, MapPin, Users, Clock, Star,
  Wine, Music, Heart, Tent, Gift, PartyPopper,
  ChevronRight, Sparkles, Crown, Check
} from "lucide-react";
import { Button } from "../components/ui/button";
import EventCalendar from "../components/events/EventCalendar";
import { sampleEvents } from "../data/sampleEvents";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 이벤트 카테고리
const categories = [
  { id: "all", label: "전체", icon: Sparkles },
  { id: "offline", label: "매칭&디너", icon: Wine },
  { id: "small", label: "소모임", icon: Heart },
  { id: "party", label: "파티", icon: PartyPopper },
  { id: "camp", label: "캠프", icon: Tent },
];


export default function EventsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") || "all";
  const subParam = searchParams.get("sub") || "";
  
  const [selectedCategory, setSelectedCategory] = useState(categoryParam);
  const [selectedSub, setSelectedSub] = useState(subParam);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [apiEvents, setApiEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [hiddenSampleIds, setHiddenSampleIds] = useState([]);


  // API에서 이벤트 목록 불러오기
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch(`${API_URL}/api/events`);
        if (response.ok) {
          const data = await response.json();
          // API 이벤트를 프론트엔드 형식으로 변환
          const formattedEvents = (data.events || []).map(event => ({
            id: event.id,
            category: event.eventCategory || mapNoticeCategoriesToEventCategory(event.noticeCategories),
            subcategory: getSubcategoryFromNoticeCategories(event.noticeCategories),
            title: event.title,
            description: event.description || "",
            date: event.date,
            time: event.time,
            location: event.location,
            venue: event.location,
            price: event.price || 0,
            currency: event.currency || "KRW",
            endDate: event.endDate || null,
            capacity: event.maxParticipants || 20,
            registered: event.currentParticipants || 0,
            image: event.image || getDefaultEventImage(event.noticeCategories),
            timezone: event.timezone || "Asia/Seoul",
            highlights: [],
            isPopular: false,
            isFromAPI: true,
            status: event.status,
            eligibility: event.eligibility || "all",
          }));
          setApiEvents(formattedEvents);
        }
      } catch (error) {
        console.error("Failed to fetch events:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();

    // 숨김 처리된 샘플 이벤트 ID 가져오기
    fetch(`${API_URL}/api/sample-events/hidden`)
      .then((r) => r.ok ? r.json() : { hidden_ids: [] })
      .then((d) => setHiddenSampleIds(d.hidden_ids || []))
      .catch(() => {});
  }, []);

  // noticeCategories를 이벤트 카테고리로 매핑
  const mapNoticeCategoriesToEventCategory = (noticeCategories) => {
    if (!noticeCategories || noticeCategories.length === 0) return "offline";
    
    for (const cat of noticeCategories) {
      if (cat.includes("event_offline")) return "offline";
      if (cat.includes("event_small")) return "small";
      if (cat.includes("event_party")) return "party";
      if (cat.includes("event_camp")) return "camp";
    }
    return "offline";
  };

  // noticeCategories에서 subcategory 추출
  const getSubcategoryFromNoticeCategories = (noticeCategories) => {
    if (!noticeCategories || noticeCategories.length === 0) return "";
    
    for (const cat of noticeCategories) {
      if (cat === "event_offline_4050") return "4050";
      if (cat === "event_offline_5060") return "5060";
      if (cat === "event_small_hobby") return "hobby";
      if (cat === "event_small_region") return "region";
      if (cat === "event_party") return "party";
      if (cat === "event_camp") return "camp";
    }
    return "";
  };

  // 이벤트 타입에 따른 기본 이미지
  const getDefaultEventImage = (noticeCategories) => {
    if (!noticeCategories) return "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=600";
    
    for (const cat of noticeCategories) {
      if (cat.includes("4050")) return "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=600";
      if (cat.includes("5060")) return "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600";
      if (cat.includes("party")) return "https://images.unsplash.com/photo-1496024840928-4c417adf211d?w=600";
      if (cat.includes("camp")) return "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=600";
    }
    return "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=600";
  };

  useEffect(() => {
    setSelectedCategory(categoryParam);
    setSelectedSub(subParam);
  }, [categoryParam, subParam]);

  // Auto-open detail modal when ?event=<id> is in URL
  useEffect(() => {
    const eventIdParam = searchParams.get("event");
    if (!eventIdParam) return;
    const all = [...apiEvents, ...sampleEvents];
    const found = all.find(e => String(e.id) === String(eventIdParam));
    if (found) {
      setSelectedEvent(found);
      setShowDetailModal(true);
    }
  }, [searchParams, apiEvents]);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedSub("");
    if (categoryId === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ category: categoryId });
    }
  };

  // API 이벤트 + 샘플 이벤트 합치기 (숨김 처리된 샘플은 제외)
  const visibleSamples = sampleEvents.filter((e) => !hiddenSampleIds.includes(e.id));
  const allEvents = [...apiEvents, ...visibleSamples];

  // 타임존 라벨 (KST 기본)
  const tzLabel = (tz) => {
    if (tz === "America/New_York") return "ET";
    if (tz === "America/Los_Angeles") return "PT";
    return "KST";
  };

  // 이벤트의 타임존 기준으로 종료(지난 이벤트) 판정
  const isEventPast = (e) => {
    if (!e?.date) return false;
    const tz = e.timezone || "Asia/Seoul";
    try {
      const todayInTz = new Date().toLocaleDateString("en-CA", { timeZone: tz });
      const compareDate = (e.endDate || e.date).slice(0, 10);
      return compareDate < todayInTz;
    } catch {
      return false;
    }
  };
  
  // 카테고리 및 서브카테고리 필터링 (+ 캘린더 날짜 필터)
  const filteredEvents = allEvents.filter(e => {
    const matchesCategory = selectedCategory === "all" || e.category === selectedCategory;
    const matchesSub = !selectedSub || e.subcategory === selectedSub || 
                       e.subcategory?.toLowerCase() === selectedSub.toLowerCase();
    const matchesDate = !selectedDate || (e.date && e.date.slice(0, 10) === selectedDate);
    return matchesCategory && matchesSub && matchesDate;
  }).sort((a, b) => {
    // 지난 이벤트는 뒤로, 향후 이벤트는 최신순(가장 늦은 일정부터)
    const aPast = a.date && isEventPast(a);
    const bPast = b.date && isEventPast(b);
    if (aPast !== bPast) return aPast ? 1 : -1;
    return String(b.date || "").localeCompare(String(a.date || ""));
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    // "YYYY-MM-DD" 문자열을 타임존 영향 없이 로컬 날짜로 파싱
    const [y, m, d] = String(dateStr).slice(0, 10).split("-").map(Number);
    const date = new Date(y, (m || 1) - 1, d || 1);
    return date.toLocaleDateString('ko-KR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      weekday: 'short'
    });
  };

  // 시작일~종료일 범위 포맷 (종료일 없거나 같으면 시작일만)
  const formatDateRange = (startStr, endStr) => {
    if (!startStr) return "";
    const start = formatDate(startStr);
    if (!endStr || endStr.slice(0, 10) === startStr.slice(0, 10)) return start;
    // 종료일은 "월 일 (요일)" 만 간단히
    const [, m, d] = String(endStr).slice(0, 10).split("-").map(Number);
    const endDate = new Date(2000, (m || 1) - 1, d || 1);
    const endShort = endDate.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' });
    return `${start} ~ ${endShort}`;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ko-KR').format(price);
  };

  // 통화별 포맷 ($1,234 / 50,000원)
  const formatPriceWithCurrency = (price, currency) => {
    const value = new Intl.NumberFormat('en-US').format(price || 0);
    return currency === "USD" ? `USD $${value}` : `₩${value}`;
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case "offline": return Wine;
      case "small": return Heart;
      case "party": return PartyPopper;
      case "camp": return Tent;
      default: return Sparkles;
    }
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case "offline": return "bg-blue-500";
      case "small": return "bg-pink-500";
      case "party": return "bg-purple-500";
      case "camp": return "bg-green-500";
      default: return "bg-burgundy-500";
    }
  };

  return (
    <div className="min-h-screen bg-stone-100" data-testid="events-page">
      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-stone-100">
        <div className="max-w-[95%] mx-auto">
          <div className="relative rounded-3xl overflow-hidden shadow-lg">
            <div className="flex flex-col md:flex-row md:h-[442px]">
              {/* Left - Text */}
              <div className="p-6 md:p-10 lg:p-12 flex flex-col justify-center md:w-[50%]" style={{background: 'linear-gradient(135deg, #e8dfd5 0%, #f0e6dc 40%, #e9ddd4 100%)'}}>
                <button
                  onClick={() => navigate("/")}
                  className="flex items-center gap-2 text-stone-500 hover:text-stone-800 transition-colors mb-5 w-fit"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="text-sm">홈으로</span>
                </button>

                <span className="text-stone-500 text-[10px] md:text-xs tracking-[0.3em] uppercase mb-3 block">New Coupling Offline Events</span>
                
                <h1 className="font-sans text-2xl md:text-3xl lg:text-[2.5rem] xl:text-5xl font-[720] text-stone-800 mb-4 leading-[1.55] tracking-tight">
                  좋은 사람을 만나는<br />가장 우아한 방법
                </h1>

                <div className="w-12 h-[2px] bg-stone-400 mb-4" />

                <p className="text-stone-600 text-sm md:text-base leading-relaxed mb-5">
                  직접 만나야 느낄 수 있는 설렘.<br />
                  오프라인에서만 가능한 특별한 인연을 만나보세요.
                </p>

                <div className="flex flex-wrap gap-2">
                  <span className="text-[11px] md:text-xs bg-stone-500/15 text-stone-700 px-3 py-1 rounded-full">매칭&디너</span>
                  <span className="text-[11px] md:text-xs bg-stone-500/15 text-stone-700 px-3 py-1 rounded-full">소모임</span>
                  <span className="text-[11px] md:text-xs bg-stone-500/15 text-stone-700 px-3 py-1 rounded-full">파티</span>
                  <span className="text-[11px] md:text-xs bg-stone-500/15 text-stone-700 px-3 py-1 rounded-full">캠프</span>
                </div>
              </div>

              {/* Right - Image */}
              <div className="md:w-[50%] min-h-[260px] md:min-h-[442px] bg-[#f5e9f0] relative overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_9ded4c02-97ba-4f9a-8fba-246dcb30d447/artifacts/jpt2y8bg_80f3318ee12aae31be1d0a19dcf24258.jpg"
                  alt="New Coupling Offline Events"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: '50% 38%' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="py-8 bg-stone-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                    isActive
                      ? "bg-purple-600 text-white shadow-lg"
                      : "bg-white text-stone-600 hover:bg-purple-50 hover:text-purple-600 border border-stone-200"
                  }`}
                  data-testid={`event-category-${cat.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Event Calendar */}
      <section className="pb-6 bg-stone-100">
        <div className="max-w-6xl mx-auto px-4">
          <EventCalendar
            events={allEvents.filter(e => selectedCategory === "all" || e.category === selectedCategory)}
            onSelectDate={setSelectedDate}
          />
        </div>
      </section>

      {/* Events Grid */}
      <section className="pb-16 bg-stone-100">
        <div className="max-w-6xl mx-auto px-4">
          {filteredEvents.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl">
              <Calendar className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">해당 카테고리의 이벤트가 없습니다.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEvents.map((event) => {
                const CategoryIcon = getCategoryIcon(event.category);
                const categoryColor = getCategoryColor(event.category);
                const remainingSpots = event.capacity - event.registered;
                // 이벤트 종료 여부 판단 (이벤트 타임존 기준)
                const isPast = isEventPast(event);
                
                return (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`bg-white rounded-2xl overflow-hidden shadow-sm border border-stone-200 hover:shadow-lg transition-all group cursor-pointer ${isPast ? "opacity-75" : ""}`}
                    onClick={() => {
                      setSelectedEvent(event);
                      setShowDetailModal(true);
                    }}
                  >
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={event.image && !event.image.startsWith("http") ? `${API_URL}${event.image}` : event.image}
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          // 이미지 로드 실패 시 카테고리 기본 이미지로 폴백
                          const fallback = getDefaultEventImage(event.noticeCategories || ["all"]);
                          if (e.currentTarget.src !== fallback) {
                            e.currentTarget.src = fallback;
                          }
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      
                      {/* Category Badge */}
                      <div className={`absolute top-3 left-3 px-3 py-1 ${categoryColor} text-white text-xs font-medium rounded-full flex items-center gap-1`}>
                        <CategoryIcon className="w-3 h-3" />
                        {event.subcategory}
                      </div>
                      
                      {/* Past / Upcoming / Popular Badge */}
                      {isPast ? (
                        <div className="absolute top-3 right-3 px-3 py-1 bg-stone-700 text-white text-xs font-bold rounded-full flex items-center gap-1">
                          종료 · Past
                        </div>
                      ) : (
                        <div className="absolute top-3 right-3 flex items-center gap-1.5">
                          <div className="px-3 py-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold rounded-full flex items-center gap-1 shadow-md">
                            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                            예정 · Upcoming
                          </div>
                          {event.isPopular && (
                            <div className="px-2.5 py-1 bg-gold-500 text-stone-900 text-xs font-bold rounded-full flex items-center gap-1">
                              <Star className="w-3 h-3 fill-current" />
                              인기
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Date */}
                      <div className="absolute bottom-3 left-3 text-white">
                        <p className="text-sm font-medium">{formatDateRange(event.date, event.endDate)}</p>
                        <p className="text-xs opacity-80">{event.time} <span className="opacity-90">({tzLabel(event.timezone)})</span></p>
                      </div>
                      
                      {/* Remaining Spots (지난 이벤트는 우상단 "종료 · Past" 뱃지로 대체됨) */}
                      {!isPast && (
                        <div className="absolute bottom-3 right-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            remainingSpots <= 3 
                              ? "bg-red-500 text-white" 
                              : "bg-white/90 text-stone-700"
                          }`}>
                            {remainingSpots <= 3 ? `마감임박 ${remainingSpots}석` : `잔여 ${remainingSpots}석`}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-bold text-lg text-stone-800 group-hover:text-purple-600 transition-colors">
                          {event.title}
                        </h3>
                        {event.eligibility && event.eligibility !== "all" && (
                          <span className="px-2.5 py-0.5 bg-burgundy-600 text-white text-xs font-bold rounded-full" data-testid="event-eligibility-badge">
                            {event.eligibility === "vip" ? "VIP 전용" : "정회원 전용"}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-stone-500 line-clamp-2 mb-4">
                        {event.description}
                      </p>
                      
                      <div className="flex items-center gap-4 text-sm text-stone-500 mb-4">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {event.location.split(' ')[1]}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {event.registered}/{event.capacity}명
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-xl font-bold text-purple-600">
                          {formatPriceWithCurrency(event.price, event.currency)}
                        </span>
                        <button className="flex items-center gap-1 text-sm text-purple-600 font-medium hover:text-purple-700">
                          자세히 보기
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Event Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Image */}
              <div className="relative h-64">
                <img
                  src={selectedEvent.image && !selectedEvent.image.startsWith("http") ? `${API_URL}${selectedEvent.image}` : selectedEvent.image}
                  alt={selectedEvent.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full backdrop-blur-sm transition-colors"
                >
                  <span className="text-white text-xl">&times;</span>
                </button>
                
                <div className="absolute bottom-4 left-4 right-4">
                  <div className={`inline-flex items-center gap-1 px-3 py-1 ${getCategoryColor(selectedEvent.category)} text-white text-xs font-medium rounded-full mb-2`}>
                    {selectedEvent.subcategory}
                  </div>
                  <h2 className="text-2xl font-[720] text-white">{selectedEvent.title}</h2>
                </div>
              </div>
              
              {/* Modal Content */}
              <div className="p-6">
                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <div className="flex items-center gap-2 text-stone-500 text-sm mb-1">
                      <Calendar className="w-4 h-4" />
                      일시
                    </div>
                    <p className="font-medium text-stone-800">{formatDateRange(selectedEvent.date, selectedEvent.endDate)}</p>
                    <p className="text-sm text-stone-600">{selectedEvent.time} <span className="text-stone-400">({tzLabel(selectedEvent.timezone)})</span></p>
                  </div>
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <div className="flex items-center gap-2 text-stone-500 text-sm mb-1">
                      <MapPin className="w-4 h-4" />
                      장소
                    </div>
                    <p className="font-medium text-stone-800">{selectedEvent.venue}</p>
                    <p className="text-sm text-stone-600">{selectedEvent.location}</p>
                  </div>
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <div className="flex items-center gap-2 text-stone-500 text-sm mb-1">
                      <Users className="w-4 h-4" />
                      참가 현황
                    </div>
                    <p className="font-medium text-stone-800">{selectedEvent.registered} / {selectedEvent.capacity}명</p>
                    <p className="text-sm text-purple-600">잔여 {selectedEvent.capacity - selectedEvent.registered}석</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-xl">
                    <div className="flex items-center gap-2 text-purple-500 text-sm mb-1">
                      <Crown className="w-4 h-4" />
                      참가비
                    </div>
                    <p className="text-2xl font-bold text-purple-600">{formatPriceWithCurrency(selectedEvent.price, selectedEvent.currency)}</p>
                  </div>
                </div>
                
                {/* Description */}
                <div className="mb-6">
                  <h3 className="font-bold text-stone-800 mb-2">이벤트 소개</h3>
                  <p className="text-stone-600 leading-relaxed">{selectedEvent.description}</p>
                </div>
                
                {/* Highlights */}
                <div className="mb-6">
                  <h3 className="font-bold text-stone-800 mb-3">포함 사항</h3>
                  <div className="space-y-2">
                    {selectedEvent.highlights.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-purple-600" />
                        </div>
                        <span className="text-stone-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowDetailModal(false)}
                    className="flex-1"
                  >
                    닫기
                  </Button>
                  {(() => {
                    const isPast = isEventPast(selectedEvent);
                    if (isPast) {
                      return (
                        <Button
                          disabled
                          className="flex-1 bg-stone-400 hover:bg-stone-400 cursor-not-allowed"
                          data-testid="event-past-disabled"
                        >
                          종료된 이벤트입니다
                        </Button>
                      );
                    }
                    return (
                      <Button
                        onClick={() => {
                          setShowDetailModal(false);
                          setShowRegisterModal(true);
                        }}
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                      >
                        참가 신청하기
                      </Button>
                    );
                  })()}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Register Modal */}
      <AnimatePresence>
        {showRegisterModal && selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowRegisterModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-[720] text-stone-800 mb-4">참가 신청</h2>
              
              <div className="p-4 bg-purple-50 rounded-xl mb-6">
                <p className="font-medium text-stone-800">{selectedEvent.title}</p>
                <p className="text-sm text-stone-600">{formatDateRange(selectedEvent.date, selectedEvent.endDate)} {selectedEvent.time} <span className="text-stone-400">({tzLabel(selectedEvent.timezone)})</span></p>
                <p className="text-lg font-bold text-purple-600 mt-2">{formatPriceWithCurrency(selectedEvent.price, selectedEvent.currency)}</p>
              </div>
              
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">이름</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                    placeholder="참가자 이름"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">연락처</label>
                  <input
                    type="tel"
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                    placeholder="010-0000-0000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">이메일</label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                    placeholder="email@example.com"
                  />
                </div>
              </form>
              
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowRegisterModal(false)}
                  className="flex-1"
                >
                  취소
                </Button>
                <Button
                  onClick={() => {
                    alert("참가 신청이 완료되었습니다!\n담당자가 곧 연락드리겠습니다.");
                    setShowRegisterModal(false);
                  }}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  결제하기
                </Button>
              </div>
              
              <p className="text-xs text-stone-400 text-center mt-4">
                * 결제 기능은 추후 업데이트 예정입니다
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
